import api from './api';
import { Issue } from '../types';

export interface IssueWithRelations extends Issue {
  project?: {
    id: string;
    name: string;
  };
  assignee?: {
    id: string;
    name: string;
    email: string;
    photoUrl?: string;
  };
  reporter?: {
    id: string;
    name: string;
    email: string;
    photoUrl?: string;
  };
  sprint?: {
    id: string;
    name: string;
  };
  comments?: Array<{
    id: string;
    content: string;
    mentions?: string[];
    createdAt: string;
    user?: {
      id: string;
      name: string;
      email: string;
      photoUrl?: string;
    };
  }>;
  history?: Array<{
    id: string;
    action: string;
    field?: string;
    createdAt: string;
    user?: {
      id: string;
      name: string;
      email: string;
    };
  }>;
  _count?: {
    comments: number;
  };
}

export const getIssues = async (filters?: {
  projectId?: string;
  status?: string;
  type?: string;
  priority?: string;
  assigneeId?: string;
  reporterId?: string;
}): Promise<IssueWithRelations[]> => {
  const params = new URLSearchParams();
  if (filters?.projectId) params.append('projectId', filters.projectId);
  if (filters?.status) params.append('status', filters.status);
  if (filters?.type) params.append('type', filters.type);
  if (filters?.priority) params.append('priority', filters.priority);
  if (filters?.assigneeId) params.append('assigneeId', filters.assigneeId);
  if (filters?.reporterId) params.append('reporterId', filters.reporterId);

  const response = await api.get(`/api/issues?${params.toString()}`);
  return response.data.data;
};

export const getIssueById = async (issueId: string): Promise<IssueWithRelations> => {
  const response = await api.get(`/api/issues/${issueId}`);
  return response.data.data;
};

export const getBugReportById = async (
  reportId: string,
  source: 'production' | 'development' = 'production'
): Promise<any> => {
  const response = await api.get(`/api/analytics/reports/${reportId}`, {
    params: { source },
  });
  return response.data.data;
};

export const createIssue = async (data: {
  title: string;
  description?: string;
  projectId: string;
  type: 'BUG' | 'FEATURE' | 'TASK' | 'EPIC' | 'STORY';
  status?: 'BACKLOG' | 'TODO' | 'IN_PROGRESS' | 'REVIEW' | 'TESTING' | 'DONE';
  priority?: 'LOWEST' | 'LOW' | 'MEDIUM' | 'HIGH' | 'HIGHEST';
  assigneeId?: string;
  sprintId?: string;
  storyPoints?: number;
  labels?: string[];
}): Promise<IssueWithRelations> => {
  const response = await api.post('/api/issues', data);
  return response.data.data;
};

export const updateIssue = async (
  issueId: string,
  data: {
    title?: string;
    description?: string;
    status?: 'BACKLOG' | 'TODO' | 'IN_PROGRESS' | 'REVIEW' | 'TESTING' | 'DONE';
    priority?: 'LOWEST' | 'LOW' | 'MEDIUM' | 'HIGH' | 'HIGHEST';
    assigneeId?: string | null;
    sprintId?: string | null;
    storyPoints?: number;
    labels?: string[];
  }
): Promise<IssueWithRelations> => {
  const response = await api.put(`/api/issues/${issueId}`, data);
  return response.data.data;
};

export const deleteIssue = async (issueId: string): Promise<void> => {
  await api.delete(`/api/issues/${issueId}`);
};

export const addIssueComment = async (issueId: string, content: string, mentionedUserIds: string[] = []) => {
  const response = await api.post(`/api/issues/${issueId}/comments`, { content, mentionedUserIds });
  return response.data.data;
};

export const getIssueComments = async (issueId: string) => {
  const response = await api.get(`/api/issues/${issueId}/comments`);
  return response.data.data;
};

export interface BugReportSyncResult {
  results: Array<{
    reportId: string;
    source?: 'production' | 'development';
    created?: boolean;
    error?: string;
  }>;
  summary: {
    total: number;
    created: number;
    alreadyProcessed: number;
    errors: number;
    sources?: {
      production: number;
      development: number;
    };
  };
}

export const syncBugReports = async (): Promise<BugReportSyncResult> => {
  const response = await api.post('/api/reports/process', undefined, {
    timeout: 120000,
  });
  return response.data.data;
};

export const getProjects = async (): Promise<Array<{ id: string; name: string; status?: string }>> => {
  const response = await api.get('/api/projects');
  return response.data.data || [];
};

export const createProject = async (data: { name: string; description?: string }): Promise<{ id: string; name: string; status?: string }> => {
  const response = await api.post('/api/projects', data);
  return response.data.data;
};
